import type {
  BaseQueries,
  CheckInFilters,
  DashboardFilters,
  OrdersStatusFilters,
  ProductWithdrawalFilters,
} from '@linka/domain/dtos';
import { queryOptions } from '@tanstack/react-query';
import { eventDashboardController } from './controller';

const keys = {
  all: ['dashboard'] as const,
  event: (id: string, params?: DashboardFilters) => [...keys.all, id, params] as const,
  orders: (eventId: string, params?: OrdersStatusFilters) => [...keys.event(eventId), 'orders', params] as const,
  checkins: (eventId: string, params?: CheckInFilters) => [...keys.event(eventId), 'checkins', params] as const,
  productWithdrawals: (eventId: string, params?: ProductWithdrawalFilters) =>
    [...keys.event(eventId), 'product-withdrawals', params] as const,
  sales: (eventId: string, params?: DashboardFilters) => [...keys.event(eventId), 'sales', params] as const,
  customFieldColumns: (eventId: string) => [...keys.event(eventId), 'custom-field-columns'] as const,
  trackingColumns: (eventId: string) => [...keys.event(eventId), 'tracking-columns'] as const,
  downloadStatus: (eventId: string, exportId: string) => [...keys.all, eventId, 'export', exportId] as const,
  productMetrics: (eventId: string) => [...keys.event(eventId), 'product-metrics'] as const,
};

export const eventDashboardQueries = {
  metrics: (eventId: string, params?: DashboardFilters) =>
    queryOptions({
      queryFn: () => eventDashboardController.dashboardMetrics({ urlParams: { eventId }, params }),
      queryKey: keys.event(eventId, params),
      select: ({ data }) => data,
      refetchInterval: 60 * 1000,
    }),
  orders: (eventId: string, params?: OrdersStatusFilters) =>
    queryOptions({
      queryFn: () => eventDashboardController.detailedOrders({ urlParams: { eventId }, params }),
      queryKey: keys.orders(eventId, params),
      select: ({ data }) => data,
    }),
  checkins: (eventId: string, params?: CheckInFilters) =>
    queryOptions({
      queryFn: () => eventDashboardController.detailedCheckins({ urlParams: { eventId }, params }),
      queryKey: keys.checkins(eventId, params),
      select: ({ data }) => data,
    }),
  productWithdrawals: (eventId: string, params?: ProductWithdrawalFilters) =>
    queryOptions({
      queryFn: () => eventDashboardController.detailedProductWithdrawals({ urlParams: { eventId }, params }),
      queryKey: keys.productWithdrawals(eventId, params),
      select: ({ data }) => data,
    }),
  sales: (eventId: string, params?: DashboardFilters & BaseQueries) =>
    queryOptions({
      queryFn: () => eventDashboardController.detailedSales({ urlParams: { eventId }, params }),
      queryKey: keys.sales(eventId, params),
      select: ({ data }) => data,
    }),
  customFieldColumns: (eventId: string) =>
    queryOptions({
      queryFn: () => eventDashboardController.customFieldColumns({ urlParams: { eventId } }),
      queryKey: keys.customFieldColumns(eventId),
      select: ({ data }) => data,
    }),
  trackingColumns: (eventId: string) =>
    queryOptions({
      queryFn: () => eventDashboardController.trackingColumns({ urlParams: { eventId } }),
      queryKey: keys.trackingColumns(eventId),
      select: ({ data }) => data,
    }),
  downloadStatus: (eventId: string, exportId: string) =>
    queryOptions({
      queryFn: () => eventDashboardController.downloadStatus({ urlParams: { eventId, exportId } }),
      queryKey: keys.downloadStatus(eventId, exportId),
      refetchInterval: 3 * 1000,
      select: (res) => res.data,
    }),
  productMetrics: (eventId: string) =>
    queryOptions({
      queryFn: () => eventDashboardController.productMetrics({ urlParams: { eventId } }),
      queryKey: keys.productMetrics(eventId),
      select: ({ data }) => data,
      refetchInterval: 60 * 1000,
    }),
};
